import React, { useEffect, useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { IFormState } from '../../types/calculator/form';

import { useCleanDisabledSelector } from './hooks/useCleanDisabledSelector';
import { useSelectSingleAvailableValue } from './hooks/useSelectSingleAvailableValue';
import { IField, IFieldElement, TComputed } from '../../types/calculator';

/** Вычисляет значение из переданной функции, если передана функция, иначе возвращает значение **/
function computeOrValue<TYPE>(value: TYPE | TComputed<TYPE> | undefined, state: IFormState) {
  return typeof value === 'function' ? (value as TComputed<TYPE>)(state) : value;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Возвращает пейлоад для аналитики для измененного значения в поле.
 * Так как обработчики кликов – универсальные, то типизация пейлоада затруднена.
 */

// TODO: https://jira.cian.tech/browse/CD-217117
// function getAnalyticsPayload({ payload, type }: { payload: any | any[]; type: EFieldType }) {
//   const fillingType = [EFieldType.InputNumber, EFieldType.YearMonthField].includes(type)
//     ? FieldFillingType.Manual
//     : FieldFillingType.Auto;
//   switch (type) {
//     case EFieldType.InputWithOptions:
//     case EFieldType.YearMonthField:
//     case EFieldType.Select:
//     case EFieldType.Suggest:
//     case EFieldType.InputNumber:
//       return {
//         value: payload[0],
//         fillingType,
//       };
//     case EFieldType.Switch:
//       return {
//         value: payload[0],
//         fillingType,
//       };
//     case EFieldType.Chips:
//     case EFieldType.Radio:
//       return {
//         value: payload[0],
//         fillingType,
//       };
//     default:
//       return {
//         value: null,
//         fillingType,
//       };
//   }
// }

export function useBaseField(props: IField) {
  const { control } = useFormContext<IFormState>();
  const {
    fieldState: { invalid, isDirty, isTouched, error },
    field: { name, value, onChange, onBlur, ref },
  } = useController<IFormState, typeof props.name>({
    control,
    name: props.name,
    shouldUnregister: true,
    rules: props.validation,
  });

  // TODO: падает из-за аналитики https://jira.cian.tech/browse/CD-217117
  // const { logFormValueChangedProductAnalytics, logFormErrorProductAnalytics } = useFormAnalytics();

  useCleanDisabledSelector({ source: props?.source, value, onChange });
  useSelectSingleAvailableValue({ source: props?.source, value, onChange, isDirty });

  const handleChange = (...payload: any | any[]) => {
    // TODO: падает из-за аналитики https://jira.cian.tech/browse/CD-217117
    // const analyticsPayload = getAnalyticsPayload({ payload, type: props.type });
    // if (typeof analyticsPayload?.value !== 'undefined' && !Number.isNaN(analyticsPayload?.value)) {
    //   logFormValueChangedProductAnalytics({
    //     param: props.name,
    //     ...analyticsPayload,
    //   });
    // }
    onChange(...payload);
  };

  useEffect(() => {
    if (error?.message && (invalid || isDirty)) {
      // logFormErrorProductAnalytics({
      //   param: props.name,
      //   error: error?.message,
      //   value,
      // });
      // TODO: Тут будем отправлять событие об ошибке
    }
  }, [invalid, isDirty, error, props.name, props.type, value]);

  return {
    fieldState: { invalid, isDirty, isTouched, error: error?.message },
    field: { name, value, onChange: handleChange, onBlur, ref },
  };
}

export interface IWithComputedField {
  field: IFieldElement;
  children: (field: IField) => React.ReactElement | null;
}
export const WithComputedField: React.FC<IWithComputedField> = ({ field, children }) => {
  const { watch } = useFormContext<IFormState>();
  const formState = watch();

  const fieldSchemeComputed = useMemo<IField>(() => {
    return {
      ...field,
      type: field.type,
      hidden: computeOrValue(field.hidden, formState),
      source: computeOrValue(field.source, formState),
      placeholder: computeOrValue(field.placeholder, formState),
      rightAdornment: computeOrValue(field.rightAdornment, formState),
      disabled: computeOrValue(field.disabled, formState),
    } satisfies IField;
  }, [field, formState]);

  if (fieldSchemeComputed.hidden) {
    return null;
  }

  return children(fieldSchemeComputed);
};
